const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/u, "");

interface ApiErrorBody { message?: string; requestId?: string; details?: Array<{ path?: string; message?: string }> }

export class ApiRequestError extends Error {
  constructor(message: string, readonly status: number, readonly requestId?: string, readonly details: string[] = []) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function responseError(body: ApiErrorBody, status: number): ApiRequestError {
  const details = body.details?.map((detail) => [detail.path, detail.message].filter(Boolean).join(": ")).filter(Boolean) ?? [];
  return new ApiRequestError(body.message ?? `Request failed with status ${status}`, status, body.requestId, details);
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  if (error instanceof ApiRequestError && error.details.length) return `${error.message}: ${error.details.join("; ")}`;
  return error.message || fallback;
}

let csrfToken: string | undefined;
let csrfTokenRequest: Promise<string> | undefined;
let refreshRequest: Promise<boolean> | undefined;

function isUnsafe(method = "GET"): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  csrfTokenRequest ??= (async () => {
    const response = await fetch(`${API_URL}/v1/auth/csrf`, { credentials: "include" });
    const body = await response.json() as { csrfToken?: string };
    if (!response.ok || !body.csrfToken) throw new Error("Unable to establish a secure browser session");
    csrfToken = body.csrfToken;
    return csrfToken;
  })();
  try {
    return await csrfTokenRequest;
  } finally {
    csrfTokenRequest = undefined;
  }
}

function resetCsrfToken(): void {
  csrfToken = undefined;
  csrfTokenRequest = undefined;
}

async function refreshAuthentication(): Promise<boolean> {
  refreshRequest ??= (async () => {
    try {
      const refreshOptions = { method: "POST" };
      const response = await fetch(`${API_URL}/v1/auth/refresh`, { ...refreshOptions, credentials: "include", headers: await secureHeaders(refreshOptions) });
      resetCsrfToken();
      return response.ok;
    } catch {
      resetCsrfToken();
      return false;
    }
  })();
  try {
    return await refreshRequest;
  } finally {
    refreshRequest = undefined;
  }
}

async function secureHeaders(options: RequestInit, json = true): Promise<Headers> {
  const headers = new Headers(options.headers);
  if (json && options.body !== undefined) headers.set("content-type", "application/json");
  if (isUnsafe(options.method)) headers.set("x-csrf-token", await getCsrfToken());
  return headers;
}

export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  let response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: await secureHeaders(options),
  });
  if (response.status === 401 && path !== "/v1/auth/login" && path !== "/v1/auth/refresh") {
    if (await refreshAuthentication()) {
      response = await fetch(`${API_URL}${path}`, { ...options, credentials: "include", headers: await secureHeaders(options) });
    }
  }
  if (response.ok && ["/v1/auth/login", "/v1/auth/refresh", "/v1/auth/logout"].includes(path)) resetCsrfToken();
  const body = await response.json().catch(() => ({})) as T & ApiErrorBody;
  if (!response.ok) throw responseError(body, response.status);
  return body;
}

export async function apiDownload(path: string): Promise<{ blob: Blob; filename: string }> {
  let response = await fetch(`${API_URL}${path}`, { credentials: "include", headers: await secureHeaders({}) });
  if (response.status === 401 && await refreshAuthentication()) response = await fetch(`${API_URL}${path}`, { credentials: "include", headers: await secureHeaders({}) });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ApiErrorBody;
    throw responseError(body, response.status);
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename = disposition.match(/filename="([^"]+)"/u)?.[1] ?? "download";
  return { blob: await response.blob(), filename };
}

export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const options = { method: "POST", body: form };
  let response = await fetch(`${API_URL}${path}`, { ...options, credentials: "include", headers: await secureHeaders(options, false) });
  if (response.status === 401 && await refreshAuthentication()) {
    response = await fetch(`${API_URL}${path}`, { ...options, credentials: "include", headers: await secureHeaders(options, false) });
  }
  const body = await response.json().catch(() => ({})) as T & ApiErrorBody;
  if (!response.ok) throw responseError(body, response.status);
  return body;
}

export async function waitForJob<T extends Record<string, unknown>>(jobId: string, timeoutMs = 120_000): Promise<T> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const job = await apiRequest<{ status: string; user_message?: string; result: T }>(`/v1/jobs/${jobId}`);
    if (job.status === "succeeded") return job.result;
    if (["failed", "dead_letter", "canceled"].includes(job.status)) throw new Error(job.user_message ?? "Background processing failed");
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  throw new Error("Background processing is still running. Check the job status later.");
}
